import { AuditLogs, Dynamo } from '../../../../primary-record-types';
import { LogCardAvatarProps } from '../LogCard/LogCardAvatar';

const getNameString = (reference: AuditLogs.ReferencedName): string => {
    return reference.preferredFirstName || reference.firstName;
};

function getSingleReference<TReference>(
    reference: AuditLogs.SingleOrListAuditLogReferenceValue<TReference>,
): AuditLogs.SingleAuditLogReferenceValue<TReference> {
    if (Array.isArray(reference)) {
        console.error('Expected single reference but got an array', reference);
        return reference[0];
    } else {
        return reference;
    }
}

function getMaybeSingleReference<TReference>(
    reference: AuditLogs.SingleOrListAuditLogReferenceValue<TReference> | undefined,
): AuditLogs.SingleAuditLogReferenceValue<TReference> | undefined {
    if (reference) {
        return getSingleReference(reference);
    } else {
        return undefined;
    }
}

export const getLogAvatarProps = (log: Dynamo.AuditLog): LogCardAvatarProps | null => {
    if (log.type === 'user-registration') {
        return null;
    } else if (log.type === 'user-profile-view') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: null,
            organizationName: null,
        };
    } else if (log.type === 'medical-profile-creation') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: null,
            organizationName: null,
        };
    } else if (log.type === 'medical-profile-deletion') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: null,
            organizationName: null,
        };
    } else if (log.type === 'collaborator-add') {
        return {
            subject: 'collaborator',
            collaboratorId: getSingleReference(log.references.collaborator).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
        };
    } else if (log.type === 'collaborator-remove') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: null,
            organizationName: null,
        };
    } else if (log.type === 'collaborator-invite') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: null,
            organizationName: null,
        };
    } else if (log.type === 'collaborator-invite-accept') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.collaboratorUserId).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: null,
            organizationName: null,
        };
    } else if (log.type === 'collaborator-invite-decline') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: null,
            organizationName: null,
        };
    } else if (log.type === 'care-team-member-collaborator-add') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: null,
            organizationName: null,
        };
    } else if (log.type === 'care-team-member-collaborator-remove') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: null,
            organizationName: null,
        };
    } else if (log.type === 'resource-creation') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (log.type === 'resource-update') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (log.type === 'resource-delete') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (log.type === 'note-creation') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (log.type === 'note-move') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (log.type === 'note-comment') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (log.type === '1up-integration-add') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (log.type === '1up-integration-refresh') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (log.type === '1up-integration-remove') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (
        log.type === 'fasten-integration-add' ||
        log.type === 'fasten-integration-refresh' ||
        log.type === 'fasten-integration-remove'
    ) {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: null,
            organizationName: null,
        };
    } else if (log.type === 'no-known-allergies-indicated') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: null,
            organizationName: null,
        };
    } else if (log.type === 'medical-profile-ownership-transfer') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: null,
            organizationName: null,
        };
    } else if (log.type === 'shared-report-creation') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (log.type === 'shared-report-viewed') {
        return {
            subject: 'profile',
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.profile).references),
        };
    } else if (log.type === 'portal-refresh-completion') {
        return {
            subject: 'profile',
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.profile).references),
        };
    } else if (log.type === 'profile-message') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (log.type === 'organization-collaborator-add') {
        return {
            subject: 'organization',
            name: getSingleReference(log.references.organization).references.name,
            profileId: getSingleReference(log.references.profile).id,
            organizationId: getSingleReference(log.references.organization).id,
            organizationName: getSingleReference(log.references.organization).references.name,
        };
    } else if (log.type === 'organization-collaborator-remove') {
        return {
            subject: 'organization',
            name: getSingleReference(log.references.organization).references.name,
            profileId: getSingleReference(log.references.profile).id,
            organizationId: getSingleReference(log.references.organization).id,
            organizationName: getSingleReference(log.references.organization).references.name,
        };
    } else if (log.type === 'medical-profile-file-upload') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (log.type === 'medical-profile-file-delete') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else if (log.type === 'organization-invoice-creation') {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getSingleReference(log.references.organization).id,
            organizationName: getSingleReference(log.references.organization).references.name,
        };
    } else if (
        log.type === 'code-status-directive-creation' ||
        log.type === 'code-status-directive-update' ||
        log.type === 'poa-directive-creation' ||
        log.type === 'poa-directive-update'
    ) {
        return {
            subject: 'user',
            userId: getSingleReference(log.references.user).id,
            profileId: getSingleReference(log.references.profile).id,
            name: getNameString(getSingleReference(log.references.user).references),
            organizationId: getMaybeSingleReference(log.references.userOrganization)?.id || null,
            organizationName: getMaybeSingleReference(log.references.userOrganization)?.references.name || null,
        };
    } else {
        return null;
    }
};
